import { useState } from "react";
import { Panel } from "omoplay";

/**
 * 고객 확정 고지서 렌더링 (A2 Phase G).
 *
 * `GET /cash/invoice` 응답(mode=INVOICE)을 그린다. 2026-07 이용분부터 고객이 보는 청구 근거는
 * **sysadmin 이 확정한 정산서 하나뿐**이라, 미확정 월에는 금액을 일절 표시하지 않고 안내만 노출한다
 * (확정 전 추정치를 보여주면 나중에 금액이 바뀌어 분쟁이 된다).
 *
 * 표시 대상은 **현재 세션에서 선택된 채널 하나**다(2026-08-03). 계정이 여러 채널 권한을 가져도
 * 다른 채널의 청구서는 나오지 않는다 — 채널 전환기로 바꿔서 본다.
 *
 * ⚠️ 표시 금액은 전부 **부가세 별도(net)** 이고 부가세는 과세 공급가 합계에 1회만 더한다. legacy 화면은
 *    단가에 부가세를 포함해 보여줬으므로, 같은 채널이라도 6월과 7월의 단가 표기가 달라 보이는 것이 정상이다.
 */
const won = (v) => `₩${Number(v ?? 0).toLocaleString()}`;

/** 항목명 + 메시지 타입. 예: `테스트 발송(알림톡 이미지)`. 타입이 없으면 이름만. */
const itemTitle = (item) => (item.typeLabel ? `${item.name}(${item.typeLabel})` : item.name);

/**
 * 구분(카테고리) 셀은 rowSpan 으로 묶인다. 이때 2번째 행부터는 셀이 하나 적어서
 * `divide-x`(셀 사이 경계선)만으로는 **왼쪽 테두리가 그려지지 않아 표가 열려 보인다**.
 * → 이어지는 행의 첫 셀에 `border-l` 을 직접 준다.
 */
const contCell = (idx, cls = "") => `px-5 py-2 ${idx === 0 ? "" : "border-l"} ${cls}`.trim();

/**
 * 항목 표.
 *  - `bill`(월 별 내역서): 구분 · 항목 · 이용기간 · 단가 · 수량 · 공급가액 (6열)
 *  - `tax`(세금계산서): 구분 · 항목 · 이용기간 · 공급가액 · 세액 (5열)
 *
 * 세금계산서에는 **품목별 합계 열을 두지 않는다** — 실제 전자세금계산서 서식이 품목마다
 * 공급가액·세액까지만 적고 합계는 하단에 한 번만 쓰기 때문이다.
 *
 * 합계 행에는 `divide-x` 를 쓰지 않는다. 셀 병합(colSpan) 때문에 세로줄이 본문 열 경계와
 * 어긋난 자리에 그어져 오히려 어색했다. 값 셀은 마지막 열 하나만 차지하게 해서 본문 숫자와 세로로 맞춘다.
 */
const InvoiceTable = ({ channel, variant }) => {
  const isTax = variant === "tax";
  const totalCols = isTax ? 5 : 6;

  return (
    <table className="w-full text-sm text-left text-gray-500 border">
      <thead className="text-xs text-gray-700 uppercase bg-gray-50 border-b">
        <tr className="divide-x">
          <th className="px-5 py-2">구분</th>
          <th className="px-5 py-2">항목</th>
          <th className="px-5 py-2">이용 기간</th>
          {!isTax && <th className="px-5 py-2 text-right">단가</th>}
          {!isTax && <th className="px-5 py-2 text-right">수량</th>}
          <th className="px-5 py-2 text-right">공급가액</th>
          {isTax && <th className="px-5 py-2 text-right">세액</th>}
        </tr>
      </thead>
      <tbody>
        {channel.groups?.map((group) =>
          group.items.map((item, idx) => (
            <tr key={`${channel.channelId}-${group.category}-${idx}`} className="bg-white border-b divide-x">
              {idx === 0 && (
                <td className="px-5 py-2 font-semibold text-gray-800 align-top" rowSpan={group.items.length}>
                  {group.categoryLabel}
                </td>
              )}
              <td className={contCell(idx)}>
                {itemTitle(item)}
                {item.vatExempt && <span className="ml-1 text-xs text-gray-400">(면세)</span>}
                {item.description && <div className="text-xs text-gray-400">{item.description}</div>}
              </td>
              <td className="px-5 py-2">{item.coverage ?? "—"}</td>
              {!isTax && <td className="px-5 py-2 text-right">{won(item.unitPriceNet)}</td>}
              {!isTax && <td className="px-5 py-2 text-right">{Number(item.quantity ?? 0).toLocaleString()}</td>}
              <td className="px-5 py-2 text-right">{won(item.supplyAmountNet)}</td>
              {isTax && <td className="px-5 py-2 text-right">{won(item.vat)}</td>}
            </tr>
          ))
        )}

        {isTax ? (
          <>
            {/* 세금계산서: 공급가액·세액을 각자의 열 아래에 합산해 둔다. */}
            <tr className="bg-gray-50 text-sm border-b border-t-2">
              <th className="px-5 py-2 text-left" colSpan={3}>합계</th>
              <th className="px-5 py-2 text-right">{won(channel.supplyTotal)}</th>
              <th className="px-5 py-2 text-right">{won(channel.vat)}</th>
            </tr>
            <tr className="bg-gray-100 text-sm">
              <th className="px-5 py-3 text-left" colSpan={3}>합계금액 (공급가액 + 세액)</th>
              <th className="px-5 py-3 text-right font-bold text-gray-900" colSpan={2}>{won(channel.total)}</th>
            </tr>
          </>
        ) : (
          <>
            <tr className="bg-gray-50 text-sm border-b border-t-2">
              <th className="px-5 py-2 text-left" colSpan={totalCols - 1}>공급가액</th>
              <th className="px-5 py-2 text-right">{won(channel.supplyTotal)}</th>
            </tr>
            <tr className="bg-gray-50 text-sm border-b">
              <th className="px-5 py-2 text-left" colSpan={totalCols - 1}>부가세</th>
              <th className="px-5 py-2 text-right">{won(channel.vat)}</th>
            </tr>
            <tr className="bg-gray-100 text-sm">
              <th className="px-5 py-3 text-left" colSpan={totalCols - 1}>합계 (부가세 포함)</th>
              <th className="px-5 py-3 text-right font-bold text-gray-900">{won(channel.total)}</th>
            </tr>
          </>
        )}
      </tbody>
    </table>
  );
};

/**
 * 세금계산서 미리보기 팝업.
 *
 * ⚠️ 실제 발행되는 세금계산서(국세청 전자세금계산서)와 **시스템적으로 연동되어 있지 않다.**
 * 확정된 정산서 내용을 세금계산서 서식과 비슷한 형태로 보여주는 **참고용**이며, 그 사실을 화면에 명시한다.
 */
const TaxInvoiceModal = ({ invoice, channel, onClose }) => (
  <div
    className="fixed inset-0 z-[2000] bg-black/50 flex items-center justify-center p-4"
    onClick={onClose}
  >
    <div
      className="bg-white rounded-lg shadow-xl w-full max-w-3xl max-h-[85vh] overflow-y-auto"
      onClick={(e) => e.stopPropagation()}
    >
      <div className="flex items-center justify-between border-b px-5 py-3">
        <div className="font-bold text-gray-900">세금계산서 (참고용)</div>
        <button className="text-gray-400 hover:text-gray-700 text-xl leading-none" onClick={onClose}>✕</button>
      </div>

      <div className="p-5">
        <div className="border-2 border-blue-700">
          <div className="bg-blue-700 text-white text-center py-2 font-bold tracking-widest">세 금 계 산 서</div>
          <div className="grid grid-cols-2 divide-x border-b text-sm">
            <div className="px-4 py-2">
              <span className="text-gray-500">공급받는자 </span>
              <b className="text-gray-900">{channel.channelName}</b>
            </div>
            <div className="px-4 py-2">
              <span className="text-gray-500">작성일자 </span>
              <b className="text-gray-900">{channel.confirmedAt ? String(channel.confirmedAt).slice(0, 10) : "-"}</b>
            </div>
          </div>
          {/* 합계는 품목 표 하단에 한 번만 둔다 — 상단에도 두면 같은 숫자가 두 번 나온다. */}
          <div className="px-4 py-2 text-xs text-gray-500 border-b">
            {invoice.month} 이용분 품목 명세
          </div>
          <InvoiceTable channel={channel} variant="tax" />
        </div>

        <p className="mt-3 text-xs text-gray-500">
          이 화면은 확정된 정산 내역을 세금계산서 서식으로 보여주는 <b>참고용</b>이며,
          실제 발행되는 전자세금계산서와 자동으로 연동되지 않습니다. 발행 관련 문의는 담당자에게 연락해 주세요.
        </p>
      </div>
    </div>
  </div>
);

export const CustomerInvoice = ({ invoice, variant = "bill", periodLabel }) => {
  const [taxOpen, setTaxOpen] = useState(false);
  const channel = invoice?.channels?.[0] ?? null;

  if (!invoice?.published || !channel) {
    return (
      <div className="mt-6 border rounded bg-gray-50 py-12 text-center">
        <div className="text-gray-700 font-semibold">정산서 작성중입니다.</div>
        <div className="mt-2 text-sm text-gray-500">보통 매월 10일 이전에 표시됩니다.</div>
      </div>
    );
  }

  return (
    <div>
      {/* 상단은 legacy 화면과 동일하게 **합계 금액 + 기간**만 둔다.
          공급가액·부가세는 아래 표 하단에 이미 나오므로 위에 또 쓰면 같은 숫자가 두 번 보인다. */}
      <div className="bg-white">
        <Panel
          items={[
            { title: `${invoice.month} 합계 금액 (부가세 포함)`, value: Number(invoice.totalPrice ?? 0).toLocaleString(), suffix: "원" },
            ...(periodLabel ? [{ title: "기간", value: periodLabel }] : []),
          ]}
        />
      </div>

      <div className="mt-3 flex items-center justify-between gap-3 flex-wrap">
        <p className="text-xs text-gray-500">
          표시된 단가·공급가액은 <b>부가세 별도</b> 금액이며, 부가세는 과세 대상 공급가액 합계에 한 번만 적용됩니다.
        </p>
        {/* 세금계산서 발행 대상 업체에만 노출한다(채널 설정 기준). */}
        {invoice.isTaxInvoice && (
          <button
            className="px-3 py-1.5 text-sm border rounded bg-white hover:bg-gray-50 whitespace-nowrap"
            onClick={() => setTaxOpen(true)}
          >
            세금계산서 확인하기
          </button>
        )}
      </div>

      <div className="mt-4">
        <div className="flex items-center justify-between bg-gray-50 border border-b-0 rounded-t px-4 py-2">
          <div className="font-semibold text-gray-800">{channel.channelName}</div>
          <div className="text-xs text-gray-500">
            {channel.billingMethod === "TAX_INVOICE" ? "세금계산서 발행" : "카드 결제"}
            {channel.confirmedAt && ` · 확정 ${String(channel.confirmedAt).slice(0, 10)}`}
          </div>
        </div>
        <InvoiceTable channel={channel} variant={variant} />
      </div>

      {/* 비고는 청구서(금액표)와 성격이 다른 정보다 — 표에 붙여 두면 청구 항목의 일부처럼 읽힌다.
          별도 카드로 떼어 놓고 제목을 달아 '안내 사항'임을 분명히 한다. */}
      {channel.customerRemark && (
        <div className="mt-6 border rounded">
          <div className="bg-gray-50 border-b px-4 py-2 font-semibold text-gray-800 text-sm">안내 사항</div>
          <div className="px-4 py-4 text-sm text-gray-700 whitespace-pre-wrap leading-relaxed">
            {channel.customerRemark}
          </div>
        </div>
      )}

      {taxOpen && <TaxInvoiceModal invoice={invoice} channel={channel} onClose={() => setTaxOpen(false)} />}
    </div>
  );
};

export default CustomerInvoice;
